import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { AlertTriangle, CheckCircle2, Database, RefreshCw, Shield, Zap } from 'lucide-react';
import { toast } from 'sonner';

const healthTone = {
  ok: 'bg-emerald-100 text-emerald-700',
  healthy: 'bg-emerald-100 text-emerald-700',
  warn: 'bg-amber-100 text-amber-700',
  warning: 'bg-amber-100 text-amber-700',
  critical: 'bg-red-100 text-red-700',
  error: 'bg-red-100 text-red-700',
  stale: 'bg-amber-100 text-amber-700',
  never: 'bg-slate-100 text-slate-600',
};

export function DataQualityPage() {
  const [loading, setLoading] = useState(true);
  const [health, setHealth] = useState(null);
  const [syncStatus, setSyncStatus] = useState([]);
  const [latestReport, setLatestReport] = useState(null);
  const [running, setRunning] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [healthRes, syncRes, reportRes] = await Promise.all([
        api.get('/integrity/health'),
        api.get('/integrity/sync-status'),
        api.get('/integrity/report/latest'),
      ]);
      setHealth(healthRes.data);
      setSyncStatus(syncRes.data?.sync_status || []);
      setLatestReport(reportRes.data || null);
    } catch (error) {
      console.error('Failed to load data quality info:', error);
      toast.error(error.response?.data?.detail || 'Failed to load data quality information');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const runReconciliation = async () => {
    setRunning(true);
    try {
      await api.post('/integrity/reconcile');
      toast.success('Reconciliation queued');
      setTimeout(loadData, 2500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to queue reconciliation');
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 p-6" data-testid="data-quality-page-loading">
        <Skeleton className="h-10 w-60" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-28 rounded-2xl" />)}</div>
        <Skeleton className="h-72 rounded-2xl" />
      </div>
    );
  }

  const entities = health?.entities || [];
  const overall = health?.overall_status || 'unknown';

  return (
    <div className="space-y-6 p-6" data-testid="data-quality-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-header-title flex items-center gap-2"><Shield className="h-6 w-6 text-[#800000]" /> Data Quality</h1>
          <p className="text-sm text-slate-500 mt-1">Monitor integrity health, sync freshness, and reconciliation status.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={loadData}><RefreshCw className="h-4 w-4 mr-2" /> Refresh</Button>
          <Button onClick={runReconciliation} disabled={running} className="bg-[#800000] hover:bg-[#991b1b] text-white" data-testid="run-reconciliation-btn">
            <Zap className={`h-4 w-4 mr-2 ${running ? 'animate-spin' : ''}`} /> {running ? 'Queueing...' : 'Run Reconciliation'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="rounded-2xl"><CardContent className="pt-6"><p className="text-xs text-slate-500 uppercase tracking-[0.15em]">Overall Health</p><div className="mt-3 flex items-center justify-between"><Badge className={healthTone[overall] || 'bg-slate-100 text-slate-700'}>{overall.toUpperCase()}</Badge>{overall === 'ok' || overall === 'healthy' ? <CheckCircle2 className="h-7 w-7 text-emerald-500" /> : <AlertTriangle className="h-7 w-7 text-amber-500" />}</div></CardContent></Card>
        <Card className="rounded-2xl"><CardContent className="pt-6"><p className="text-xs text-slate-500 uppercase tracking-[0.15em]">Collections Checked</p><p className="text-3xl font-bold mt-3">{entities.length}</p></CardContent></Card>
        <Card className="rounded-2xl"><CardContent className="pt-6"><p className="text-xs text-slate-500 uppercase tracking-[0.15em]">Latest Report</p><p className="text-sm font-semibold mt-3">{latestReport?.overall_status ? latestReport.overall_status.toUpperCase() : 'No report yet'}</p></CardContent></Card>
        <Card className="rounded-2xl"><CardContent className="pt-6"><p className="text-xs text-slate-500 uppercase tracking-[0.15em]">Last Checked</p><p className="text-sm font-semibold mt-3">{health?.checked_at ? new Date(health.checked_at).toLocaleString() : '—'}</p></CardContent></Card>
      </div>

      <Tabs defaultValue="integrity">
        <TabsList className="rounded-2xl bg-slate-100 p-1 h-auto">
          <TabsTrigger value="integrity">Integrity Overview</TabsTrigger>
          <TabsTrigger value="freshness">Sync Freshness</TabsTrigger>
          <TabsTrigger value="report">Latest Report</TabsTrigger>
        </TabsList>

        <TabsContent value="integrity" className="mt-5">
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle>Collection Health</CardTitle>
              <CardDescription>Live count checks against Odoo-backed canonical data.</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Entity</TableHead>
                    <TableHead>Source Count</TableHead>
                    <TableHead>Canonical Count</TableHead>
                    <TableHead>Delta</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entities.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-500">No integrity entities available</TableCell></TableRow> : entities.map((entity) => (
                    <TableRow key={entity.entity}>
                      <TableCell className="font-medium">{entity.label || entity.entity}</TableCell>
                      <TableCell>{entity.source_count ?? '—'}</TableCell>
                      <TableCell>{entity.canonical_count ?? '—'}</TableCell>
                      <TableCell>{entity.delta ?? entity.diff ?? '—'}</TableCell>
                      <TableCell><Badge className={healthTone[entity.status] || 'bg-slate-100 text-slate-700'}>{(entity.status || 'unknown').toUpperCase()}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="freshness" className="mt-5">
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle>Sync Freshness</CardTitle>
              <CardDescription>Latest sync timestamps from the sync state collection.</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Entity</TableHead>
                    <TableHead>Last Sync</TableHead>
                    <TableHead>Age</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {syncStatus.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-500">No sync state available</TableCell></TableRow> : syncStatus.map((entity) => (
                    <TableRow key={entity.entity}>
                      <TableCell className="font-medium">{entity.entity}</TableCell>
                      <TableCell>{entity.last_sync ? new Date(entity.last_sync).toLocaleString() : 'Never'}</TableCell>
                      <TableCell>{entity.ago_label}</TableCell>
                      <TableCell><Badge className={healthTone[entity.status] || 'bg-slate-100 text-slate-700'}>{(entity.status || 'unknown').toUpperCase()}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="report" className="mt-5">
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle>Latest Reconciliation Report</CardTitle>
              <CardDescription>Most recent reconciliation summary captured by the backend.</CardDescription>
            </CardHeader>
            <CardContent>
              {latestReport?.message ? (
                <div className="rounded-xl border border-dashed bg-slate-50 px-4 py-5 text-sm text-slate-500">{latestReport.message}</div>
              ) : (
                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-2"><Database className="h-4 w-4 text-[#800000]" /><span className="font-medium">Status:</span><Badge className={healthTone[latestReport?.overall_status] || 'bg-slate-100 text-slate-700'}>{(latestReport?.overall_status || 'unknown').toUpperCase()}</Badge></div>
                  <p><span className="font-medium">Entities Checked:</span> {latestReport?.entities_checked || 0}</p>
                  <p><span className="font-medium">Ghosts Fixed:</span> {latestReport?.total_ghosts_fixed || 0}</p>
                  <p><span className="font-medium">Elapsed:</span> {latestReport?.elapsed_seconds ? `${latestReport.elapsed_seconds}s` : '—'}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default DataQualityPage;